import React, { useState, useEffect, useCallback } from "react";
import {
  Search,
  Filter,
  RotateCcw,
  Calendar,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Eye,
  X,
  User,
  Mail,
  Phone,
  Building2,
  Tag,
  MessageSquare,
  Clock,
  Download,
  CheckCircle2,
  AlertCircle,
  Briefcase,
  CheckSquare2Icon,
  CheckSquare,
  ChevronDown,
  MessageCircleIcon,
} from "lucide-react";
import { assignLead, getWebLeadReport } from "../../services/webLeadReport";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";

export default function WebLeadReport() {
  // Filter States
  const [filters, setFilters] = useState({
    type: "", // Default to 'Lead', can be 'Lead', 'Enquiry', or '' (All)
    status: "", // '', 'New', etc.
    search: "",
    employee: "",
    from_date: "",
    to_date: "",
    page: 1,
    per_page: 20,
  });

  // Data & API States
  const [reportData, setReportData] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 20,
    total: 0,
    last_page: 1,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Modal Detail State
  const [selectedLead, setSelectedLead] = useState(null);
  const [allEmployee, setAllEmployee] = useState([]);

  const [assignToEmployee, setAssignToEmployee] = useState("");

  const [assignLeadsArray, setAssignLeadsArray] = useState([]);
  const [assignLeadLoading, setAssignLeadLoading] = useState(false);
  const [selectAllClicked, setSelectAllClicked] = useState(false);

  const handleAssignLeadsArray = (item) => {
    setAssignLeadsArray((prev) => {
      const exists = prev.some((prevItem) => prevItem.id === item.id);

      if (exists) {
        return prev.filter((prevItem) => prevItem.id !== item.id);
      }
      return [...prev, item];
    });
  };

  // Fetch Report Function
  const fetchReport = useCallback(async (currentFilters = filters) => {
    setLoading(true);
    setError(null);

    try {
      // Clean up empty filters before sending to API
      const payload = Object.fromEntries(
        Object.entries(currentFilters).filter(
          ([_, v]) => v !== "" && v !== null,
        ),
      );

      const res = await getWebLeadReport(payload);

      // Handle standard response wrapper
      if (res && res.data) {
        setReportData(res.data.data || []);
        setAllEmployee(res?.data?.referred_by_options);
        setPagination({
          current_page: res.data.current_page || 1,
          per_page: res.data.per_page || 20,
          total: res.data.total || 0,
          last_page: res.data.last_page || 1,
        });
      }
    } catch (err) {
      console.error("Error fetching web lead report:", err);
      setError(
        "Failed to load report data. Please check your network or try again.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  const handleAssignService = async () => {
    setAssignLeadLoading(true);
    const payload = {};
    payload.employee_id = assignToEmployee;

    const leads = assignLeadsArray?.map((item) => ({
      id: item.id,
      type: item.type,
    }));

    payload.records = leads;
    console.log(payload);

    try {
      const res = await assignLead(payload);
      if (res?.status?.code === 200) {
        fetchReport();
        setSelectAllClicked(false);
        setAssignLeadsArray([]);
        setAssignToEmployee("");
        showSuccessToast(
          res?.message || res?.data?.message || res?.data?.data?.message,
        );
      } else {
        showErrorToast(
          res?.message || res?.data?.message || res?.data?.data?.message,
        );
      }
    } catch (error) {
      console.log(error);
    } finally {
      setAssignLeadLoading(false);
    }
  };

  // Fetch on mount and when page changes
  useEffect(() => {
    fetchReport(filters);
  }, [filters.page]);

  // Filter Handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleApplyFilters = (e) => {
    e.preventDefault();
    // Reset to page 1 when new filters are applied
    const updatedFilters = { ...filters, page: 1 };
    setFilters(updatedFilters);
    fetchReport(updatedFilters);
  };

  const handleResetFilters = () => {
    const defaultFilters = {
      type: "",
      status: "",
      search: "",
      from_date: "",
      employee: "",
      to_date: "",
      page: 1,
      per_page: 20,
    };
    setFilters(defaultFilters);
    fetchReport(defaultFilters);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.last_page) {
      setFilters((prev) => ({ ...prev, page: newPage }));
    }
  };

  // Helper Functions for Data Display
  const getDisplayName = (item) => {
    if (item.name && item.name.trim() !== "") return item.name;
    const combined = `${item.name}`.trim();
    return combined.length > 0 ? combined : "N/A";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return dateString;
    }
  };

  const exportToCSV = () => {
    if (!reportData || reportData.length === 0) return;

    const headers = [
      "ID",
      "Type",
      "Name",
      "Mobile",
      "Email",
      "Department",
      "Status",
      "Created At",
    ];
    const rows = reportData.map((item) => [
      item.id,
      item.type,
      `"${getDisplayName(item)}"`,
      item.mobile || item.alternate_mobile || "",
      item.email || "",
      item.department || "",
      item.status,
      item.created_at,
    ]);

    const csvContent =
      "data:text/csv;charset=utf-8," +
      [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute(
      "download",
      `Web_Lead_Report_${new Date().toISOString().slice(0, 10)}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 p-4 md:p-8 font-sans">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header Bar */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 bg-white p-6 rounded-2xl border border-slate-200/80 shadow-sm">
          <div>
            <div className="flex items-center gap-2">
              <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
                <Briefcase className="w-6 h-6" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
                Web Lead & Enquiry Report
              </h1>
            </div>
            <p className="text-sm text-slate-500 mt-1">
              Filter, track, and analyze web form submissions and incoming
              enquiries.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={exportToCSV}
              disabled={reportData.length === 0}
              className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 active:bg-slate-100 disabled:opacity-50 transition shadow-sm"
            >
              <Download className="w-4 h-4 text-slate-500" />
              Export CSV
            </button>
          </div>
        </div>

        {/* Filters Form */}
        <div className="bg-white p-6 rounded-2xl border border-slate-200/80 shadow-sm space-y-4">
          <div className="flex items-center justify-between pb-3 border-b border-slate-100">
            <div className="flex items-center gap-2 text-slate-800 font-semibold text-sm">
              <Filter className="w-4 h-4 text-blue-600" />
              Filter Reports
            </div>
            <span className="text-xs text-slate-400">
              Total Results: {pagination.total}
            </span>
          </div>

          <form
            onSubmit={handleApplyFilters}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4"
          >
            {/* Search Input */}
            <div className="xl:col-span-2 space-y-1">
              <label className="text-xs font-semibold text-slate-700">
                Search Keyword
              </label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  name="search"
                  value={filters.search}
                  onChange={handleInputChange}
                  placeholder="Name, mobile, email..."
                  className="w-full pl-9 pr-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-slate-800 placeholder-slate-400 transition"
                />
              </div>
            </div>

            {/* Type Select */}
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-700">
                Report Type
              </label>
              <select
                name="type"
                value={filters.type}
                onChange={handleInputChange}
                className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-slate-800 transition"
              >
                <option value="">All Types</option>
                <option value="Lead">Lead</option>
                <option value="Enquiry">Enquiry</option>
                <option value="Signup">Signup</option>
              </select>
            </div>

            {/* Status Select */}
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-700">
                Status
              </label>
              <select
                name="status"
                value={filters.status}
                onChange={handleInputChange}
                className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-slate-800 transition"
              >
                <option value="">All Statuses</option>
                <option value="Assigned">Assigned</option>
                <option value="Under Discussion">Under Discussion</option>
                <option value="Closed">Closed</option>
                <option value="Pending">Pending</option>
              </select>
            </div>

            {/* From Date */}
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-700">
                From Date
              </label>
              <div className="relative">
                <input
                  type="date"
                  name="from_date"
                  value={filters.from_date}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-slate-800 transition"
                />
              </div>
            </div>

            {/* To Date */}
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-700">
                To Date
              </label>
              <div className="relative">
                <input
                  type="date"
                  name="to_date"
                  value={filters.to_date}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-slate-800 transition"
                />
              </div>
            </div>

            {/* employee */}
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-700">
                Employee
              </label>
              <div className="relative">
                <select
                  onChange={handleInputChange}
                  value={filters.employee}
                  className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-slate-800 transition"
                  name="employee"
                  id=""
                >
                  <option value="">Employee</option>

                  {allEmployee?.length > 0 &&
                    allEmployee?.map((item, idx) => {
                      return (
                        <option value={item?.user_id}>
                          {item?.name} {item?.user_id}
                        </option>
                      );
                    })}
                </select>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="xl:col-span-6 lg:col-span-3 flex items-center justify-between pt-2 border-t border-slate-100 mt-2">
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500 font-medium">
                  Per Page:
                </span>
                <select
                  name="per_page"
                  value={filters.per_page}
                  onChange={(e) => {
                    handleInputChange(e);
                    fetchReport({
                      ...filters,
                      per_page: Number(e.target.value),
                      page: 1,
                    });
                  }}
                  className="px-2 py-1 text-xs bg-slate-100 border border-slate-200 rounded-lg focus:outline-none text-slate-700 font-semibold"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleResetFilters}
                  disabled={loading}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-slate-600 bg-slate-100 rounded-xl hover:bg-slate-200 focus:outline-none disabled:opacity-50 transition"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  Reset
                </button>

                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex items-center justify-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 active:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500/40 disabled:opacity-75 transition shadow-sm min-w-[120px]"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Fetching...</span>
                    </>
                  ) : (
                    <>
                      <Filter className="w-4 h-4" />
                      <span>Apply Filters</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>

          {/* assign to employee */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-5 p-5 bg-white border border-slate-100 rounded-2xl shadow-sm">
            {/* Header / Context */}
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center gap-2.5 flex-wrap">
                <h3 className="text-sm font-semibold text-slate-800">
                  Assign Service
                </h3>

                {/* Dynamic Selected Count Pill */}
                <div
                  className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs font-medium rounded-full border transition-all duration-200 ${
                    assignLeadsArray?.length > 0
                      ? "bg-blue-50 text-blue-700 border-blue-200/80 shadow-2xs"
                      : "bg-slate-100 text-slate-500 border-slate-200/60"
                  }`}
                >
                  <CheckSquare
                    className={`w-3.5 h-3.5 ${
                      assignLeadsArray?.length > 0
                        ? "text-blue-600"
                        : "text-slate-400"
                    }`}
                  />
                  <span>
                    <strong className="font-semibold">
                      {assignLeadsArray?.length || 0}
                    </strong>{" "}
                    <span className="opacity-90">
                      {assignLeadsArray?.length === 1 ? "Selected" : "Selected"}
                    </span>
                  </span>
                </div>
              </div>

              <p className="text-xs text-slate-500">
                Select an employee to handle this request.
              </p>
            </div>

            {/* Input & Action Area */}
            <div className="w-full md:w-auto space-y-1.5">
              <label
                htmlFor="assign-employee"
                className="block text-xs font-semibold text-slate-600 uppercase tracking-wide"
              >
                Assign To
              </label>

              <div className="flex flex-col sm:flex-row gap-3">
                {/* Select Box */}
                <div className="relative w-full sm:w-64">
                  <select
                    id="assign-employee"
                    name="assignToEmployee"
                    value={assignToEmployee}
                    onChange={(e) => setAssignToEmployee(e.target.value)}
                    className="w-full appearance-none px-4 py-2.5 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 text-slate-800 transition-all duration-200 shadow-sm cursor-pointer"
                  >
                    <option value="">Select an employee...</option>

                    {allEmployee?.length > 0 &&
                      allEmployee.map((item) => (
                        <option key={item?.user_id} value={item?.user_id}>
                          {item?.name} ({item?.user_id})
                        </option>
                      ))}
                  </select>

                  {/* Lucide Dropdown Arrow */}
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4">
                    <ChevronDown className="w-4 h-4 text-slate-400" />
                  </div>
                </div>

                <button
                  type="button"
                  disabled={
                    !assignToEmployee ||
                    assignLeadsArray.length === 0 ||
                    assignLeadLoading
                  }
                  onClick={() => handleAssignService()}
                  className="px-6 py-2.5 min-w-[120px] flex items-center justify-center bg-blue-600 text-white text-sm font-medium rounded-xl hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-500/20 active:bg-blue-800 transition-all duration-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {assignLeadLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Please wait...
                    </span>
                  ) : (
                    "Assign"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="p-4 bg-rose-50 border border-rose-200 rounded-2xl flex items-center gap-3 text-rose-700 text-sm">
            <AlertCircle className="w-5 h-5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Data Table Section */}
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden relative">
          {/* Table Loader Overlay when refetching */}
          {loading && reportData.length > 0 && (
            <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] flex items-center justify-center z-10 transition">
              <div className="flex items-center gap-3 px-4 py-2 bg-white border border-slate-200 rounded-full shadow-lg">
                <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                <span className="text-sm font-medium text-slate-700">
                  Updating table...
                </span>
              </div>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/80 border-b border-slate-200/80 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  {assignToEmployee && (
                    <th className="py-4 px-6">
                      <button
                        type="button"
                        onClick={() => {
                          const nextSelectAll = !selectAllClicked;

                          setSelectAllClicked(nextSelectAll);

                          if (nextSelectAll) {
                            setAssignLeadsArray(
                              // reportData?.filter((item) => !item.is_assigned),
                              reportData
                            );
                          } else {
                            setAssignLeadsArray([]);
                          }
                        }}
                      >
                        <CheckSquare />
                      </button>
                    </th>
                  )}
                  <th className="py-4 px-6">ID</th>
                  <th className="py-4 px-6 text-nowrap">Assigned To</th>
                  <th className="py-4 px-6">Type</th>
                  <th className="py-4 px-6">Name</th>
                  <th className="py-4 px-6">Contact Info</th>
                  <th className="py-4 px-6">Department</th>
                  <th className="py-4 px-6">Message</th>
                  <th className="py-4 px-6">Status</th>
                  <th className="py-4 px-6">Created Date</th>
                  <th className="py-4 px-6 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                {loading && reportData.length === 0 ? (
                  // Skeleton Loading Rows
                  Array.from({ length: 5 }).map((_, idx) => (
                    <tr key={idx} className="animate-pulse">
                      <td className="py-4 px-6">
                        <div className="h-4 bg-slate-200 rounded w-8"></div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="h-6 bg-slate-200 rounded-full w-16"></div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="h-4 bg-slate-200 rounded w-32"></div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="h-4 bg-slate-200 rounded w-28"></div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="h-4 bg-slate-200 rounded w-20"></div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="h-6 bg-slate-200 rounded-full w-16"></div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="h-4 bg-slate-200 rounded w-28"></div>
                      </td>
                      <td className="py-4 px-6 text-right">
                        <div className="h-8 bg-slate-200 rounded-lg w-12 ml-auto"></div>
                      </td>
                    </tr>
                  ))
                ) : reportData.length === 0 ? (
                  // Empty State
                  <tr>
                    <td colSpan={8} className="py-12 px-6 text-center">
                      <div className="flex flex-col items-center justify-center max-w-xs mx-auto space-y-3">
                        <div className="p-4 bg-slate-100 rounded-full text-slate-400">
                          <Search className="w-8 h-8" />
                        </div>
                        <h3 className="text-base font-semibold text-slate-800">
                          No Records Found
                        </h3>
                        <p className="text-xs text-slate-500 text-center">
                          We couldn't find any leads or enquiries matching your
                          current filter criteria.
                        </p>
                        <button
                          onClick={handleResetFilters}
                          className="px-3.5 py-1.5 text-xs font-semibold text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition"
                        >
                          Clear Filters
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  // Table Content
                  reportData.map((row) => (
                    <tr
                      key={row.id}
                      className="hover:bg-slate-50/80 transition-colors group"
                    >
                      {assignToEmployee && (
                        <td className="py-4 px-6 font-mono text-xs font-semibold text-slate-500 align-middle">
                          <input
                            checked={assignLeadsArray?.some(
                              (item) => item?.id === row.id,
                            )}
                            onChange={() => handleAssignLeadsArray(row)}
                            type="checkbox"
                            // disabled={row?.is_assigned}
                            name=""
                            id=""
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 cursor-pointer accent-blue-600 transition duration-150 ease-in-out"
                          />
                        </td>
                      )}

                      <td className="py-4 px-6 font-mono text-xs font-semibold text-slate-500">
                        #{row.id}
                      </td>

                      <td className="py-4 px-6 align-middle">
                        {row?.referred_by_selected?.name ? (
                          <div className="inline-flex flex-col gap-0.5 px-3 py-1 bg-emerald-50 border border-emerald-100/80 rounded-lg shadow-sm">
                            <span className="text-xs font-semibold text-emerald-800 whitespace-nowrap">
                              {row?.referred_by_selected?.name}
                            </span>
                            <span className="text-[12px] font-mono font-medium text-emerald-600/90 tracking-wide">
                              {row?.referred_by_selected?.user_id}
                            </span>
                          </div>
                        ) : (
                          <span className="inline-flex px-2.5 py-1 text-xs font-medium text-slate-400 bg-slate-50 border border-slate-100 rounded-md">
                            N/A
                          </span>
                        )}
                      </td>
                      {/* Type Badge */}
                      <td className="py-4 px-6">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${
                            row.type === "Lead"
                              ? "bg-indigo-50 text-indigo-700 border-indigo-200"
                              : row.type === "Signup"
                                ? "bg-green-50 text-green-700 border-green-200"
                                : "bg-amber-50 text-amber-700 border-amber-200"
                          }`}
                        >
                          <Tag className="w-3 h-3" />
                          {row.type || "N/A"}
                        </span>
                      </td>

                      {/* Full Name */}
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-600 font-bold text-xs uppercase shrink-0">
                            {getDisplayName(row).charAt(0)}
                          </div>
                          <div>
                            <span className="font-semibold text-slate-900 block leading-snug">
                              {getDisplayName(row)}
                            </span>
                          </div>
                        </div>
                      </td>

                      {/* Contact Info */}
                      <td className="py-4 px-6 space-y-1">
                        {(row.mobile || row.alternate_mobile) && (
                          <div className="flex items-center gap-1.5 text-xs font-medium text-slate-700">
                            <Phone className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                            <span>{row.mobile || row.alternate_mobile}</span>
                          </div>
                        )}
                        {row.email ? (
                          <div className="flex items-center gap-1.5 text-xs text-slate-500">
                            <Mail className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                            <span className="truncate max-w-[160px]">
                              {row.email}
                            </span>
                          </div>
                        ) : !row.mobile && !row.alternate_mobile ? (
                          <span className="text-xs text-slate-400 italic">
                            No contact info
                          </span>
                        ) : null}
                      </td>

                      {/* Department */}
                      <td className="py-4 px-6">
                        {row.department || row?.lead_department ? (
                          <span className="inline-flex items-center gap-1 text-xs text-slate-700 font-medium">
                            <Building2 className="w-3.5 h-3.5 text-slate-400" />
                            {row.department || row?.lead_department}
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </td>
                      <td className="py-4 px-6">
                        {row.message ? (
                          <span className="inline-flex items-center gap-1 text-xs text-slate-700 font-medium">
                            <MessageCircleIcon className="w-3.5 h-3.5 text-slate-400" />
                            {row.message}
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </td>

                      {/* Status */}
                      <td className="py-4 px-6">
                        <span
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-nowrap text-xs font-medium border ${
                            row.status === "Assigned"
                              ? "bg-emerald-50 text-emerald-700 border-emerald-200" :  row.status === "Under Discussion" ? "bg-amber-50 text-amber-700 border-amber-400"  :  row.status === "Closed" ? "bg-rose-50 text-rose-700 border-rose-400" 
                              : "bg-slate-100 text-slate-700 border-slate-200"
                          }`}
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${
                              row.status === "Assigned"
                                ? "bg-emerald-500" :  row.status === "Under Discussion" ? "bg-amber-600" :  row.status === "Closed" ? "bg-rose-600"
                                : "bg-slate-400"
                            }`}
                          />
                          {row.status || "Pending"}
                        </span>
                      </td>

                      {/* Created At */}
                      <td className="py-4 px-6 text-xs text-slate-600 whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          <Clock className="w-3.5 h-3.5 text-slate-400" />
                          <span>{formatDate(row.created_at)}</span>
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="py-4 px-6 text-right">
                        <button
                          onClick={() => setSelectedLead(row)}
                          className="p-2 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Footer */}
          <div className="px-6 py-4 bg-slate-50/80 border-t border-slate-200/80 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-slate-600 font-medium">
              Showing page{" "}
              <span className="font-bold text-slate-900">
                {pagination.current_page}
              </span>{" "}
              of{" "}
              <span className="font-bold text-slate-900">
                {pagination.last_page}
              </span>{" "}
              ({pagination.total} total items)
            </p>

            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(pagination.current_page - 1)}
                disabled={pagination.current_page <= 1 || loading}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 transition shadow-sm"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </button>

              <div className="flex items-center gap-1 px-2">
                {Array.from({ length: pagination.last_page }, (_, i) => i + 1)
                  .filter(
                    (p) =>
                      p === 1 ||
                      p === pagination.last_page ||
                      Math.abs(p - pagination.current_page) <= 1,
                  )
                  .map((p, idx, arr) => {
                    const showEllipsis = idx > 0 && p - arr[idx - 1] > 1;
                    return (
                      <React.Fragment key={p}>
                        {showEllipsis && (
                          <span className="text-xs text-slate-400 px-1">
                            ...
                          </span>
                        )}
                        <button
                          onClick={() => handlePageChange(p)}
                          className={`w-7 h-7 text-xs font-semibold rounded-lg transition ${
                            pagination.current_page === p
                              ? "bg-blue-600 text-white"
                              : "text-slate-600 hover:bg-slate-200"
                          }`}
                        >
                          {p}
                        </button>
                      </React.Fragment>
                    );
                  })}
              </div>

              <button
                onClick={() => handlePageChange(pagination.current_page + 1)}
                disabled={
                  pagination.current_page >= pagination.last_page || loading
                }
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 transition shadow-sm"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      {selectedLead && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="bg-white w-full max-w-xl rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
            {/* Modal Header */}
            <div className="px-6 py-4 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span
                  className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${
                    selectedLead.type === "Lead"
                      ? "bg-indigo-50 text-indigo-700 border-indigo-200"
                      : "bg-amber-50 text-amber-700 border-amber-200"
                  }`}
                >
                  {selectedLead.type}
                </span>
                <h3 className="font-bold text-slate-900 text-base">
                  Details #{selectedLead.id}
                </h3>
              </div>
              <button
                onClick={() => setSelectedLead(null)}
                className="p-1 text-slate-400 hover:text-slate-600 rounded-lg transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-6 max-h-[75vh] overflow-y-auto">
              {/* User Info Block */}
              <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl border border-slate-100">
                <div className="w-12 h-12 rounded-full bg-blue-100 text-blue-700 font-bold text-lg flex items-center justify-center shrink-0">
                  {getDisplayName(selectedLead).charAt(0)}
                </div>
                <div className="space-y-1">
                  <h4 className="font-bold text-slate-900 text-lg leading-tight">
                    {getDisplayName(selectedLead)}
                  </h4>
                  <p className="text-xs text-slate-500">
                    Status:{" "}
                    <span className="font-medium text-slate-700">
                      {selectedLead.status}
                    </span>
                  </p>
                </div>
              </div>

              {/* Grid Information */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="space-y-1">
                  <span className="text-xs font-semibold text-slate-400 uppercase">
                    Primary Mobile
                  </span>
                  <p className="font-medium text-slate-800 flex items-center gap-1.5">
                    <Phone className="w-3.5 h-3.5 text-slate-400" />
                    {selectedLead.mobile || "N/A"}
                  </p>
                </div>

                <div className="space-y-1">
                  <span className="text-xs font-semibold text-slate-400 uppercase">
                    Alternate Mobile
                  </span>
                  <p className="font-medium text-slate-800 flex items-center gap-1.5">
                    <Phone className="w-3.5 h-3.5 text-slate-400" />
                    {selectedLead.alternate_mobile || "N/A"}
                  </p>
                </div>

                <div className="space-y-1">
                  <span className="text-xs font-semibold text-slate-400 uppercase">
                    Email Address
                  </span>
                  <p className="font-medium text-slate-800 flex items-center gap-1.5 truncate">
                    <Mail className="w-3.5 h-3.5 text-slate-400" />
                    {selectedLead.email || "N/A"}
                  </p>
                </div>

                <div className="space-y-1">
                  <span className="text-xs font-semibold text-slate-400 uppercase">
                    Department
                  </span>
                  <p className="font-medium text-slate-800 flex items-center gap-1.5">
                    <Building2 className="w-3.5 h-3.5 text-slate-400" />
                    {selectedLead.department || "N/A"}
                  </p>
                </div>
              </div>

              {/* Message / Description Block */}
              {(selectedLead.message || selectedLead.description) && (
                <div className="space-y-2 pt-2 border-t border-slate-100">
                  <span className="text-xs font-semibold text-slate-400 uppercase flex items-center gap-1">
                    <MessageSquare className="w-3.5 h-3.5 text-slate-400" />
                    Message / Description
                  </span>
                  <div className="p-3.5 bg-slate-50 border border-slate-200/70 rounded-xl text-slate-700 text-sm whitespace-pre-wrap leading-relaxed">
                    {selectedLead.message || selectedLead.description}
                  </div>
                </div>
              )}

              {/* Timestamps */}
              <div className="text-xs text-slate-400 pt-3 border-t border-slate-100 flex items-center justify-between">
                <span>Created: {formatDate(selectedLead.created_at)}</span>
                <span>Updated: {formatDate(selectedLead.updated_at)}</span>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-3 bg-slate-50 border-t border-slate-100 flex justify-end">
              <button
                onClick={() => setSelectedLead(null)}
                className="px-4 py-2 text-sm font-semibold text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-100 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
